/**
 * Admin Services Page - Trade Loop v2.0
 *
 * Admin dashboard for viewing system statistics, user data, support messages,
 * connections, referrals, and chat messages management
 */

import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useRealtimeSubscription, type TableSubscription } from "@/hooks/useRealtimeSubscription";
import { useToast } from "@/contexts/ToastContext";
import { useAuth } from "@/contexts/AuthContext";
import { api, describeApiError } from "@/lib/api";
import AppLayout from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PlanGrantsPanel } from "@/components/admin/PlanGrantsPanel";
import { highlightSearchTerms } from "@/config/search/search-utils";
import { Badge } from "@/components/ui/badge";
import {
  Users,
  MessageSquare,
  BarChart3,
  ArrowLeftRight,
  UserPlus,
  Clock,
  CheckCircle,
  AlertCircle,
  Search,
  Mail,
  Calendar,
  RefreshCw,
  Link2,
  Send,
  Trash2,
  Ban,
  MessageCircle,
  Archive,
  Lock,
  Unlock,
  MoreVertical,
  List,
  Plus,
  Shield,
  FileText,
  Filter,
  XCircle,
  ChevronLeft,
  ChevronRight,
  UserCog,
  Loader2,
  MailCheck,
  Download,
} from "lucide-react";
import { impersonateUser } from "@/services/authService";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { AccountActionWizard } from "@/components/admin/AccountActionWizard";
import type { LockAccountData } from "@/components/admin/AccountActionWizard";
import { DeleteConfirmModal } from "@/components/ConfirmModal";
import ReferralDetailModal from "@/components/ReferralDetailModal";
import UserPermissionsModal from "@/components/UserPermissionsModal";

interface AdminReply {
  id: string;
  userId: string;
  reply: string;
  sentAt: string;
  createdAt: string;
  user?: {
    id: string;
    firstName: string | null;
    lastName: string | null;
  } | null;
}

interface SupportMessage {
  id: string;
  name: string;
  email: string;
  subject: string;
  message: string;
  status: string;
  adminNotes: string | null;
  createdAt: string;
  respondedAt: string | null;
  replies?: AdminReply[];
  user?: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
  } | null;
  responder?: {
    id: string;
    firstName: string | null;
    lastName: string | null;
  } | null;
}

/** Roles that confer admin access; mirrors the backend's RoleService. */
const ADMIN_ROLE_NAMES = ["admin", "super admin", "system auditor"];

// Realtime table configs, hoisted to module scope so they keep the same identity
// across renders. useRealtimeSubscription rebuilds its `subscribe` callback (and
// therefore re-runs its subscribe effect) whenever `tables` changes identity, so
// passing these inline made every render tear the channel down and rejoin it.
// Supabase answers a rapid rejoin loop with ClientJoinRateLimitReached, whose
// error path schedules a reconnect, which re-renders, which rejoins — a loop that
// never settles. These are static, so a module constant is all the memoization
// they need.
const SUPPORT_MESSAGES_TABLES: TableSubscription[] = [{ table: "support_messages", event: "*" }];
const CONNECTIONS_TABLES: TableSubscription[] = [{ table: "connections", event: "*" }];
const REFERRALS_TABLES: TableSubscription[] = [{ table: "referrals", event: "*" }];
const CHAT_MESSAGES_TABLES: TableSubscription[] = [{ table: "chat_messages", event: "*" }];
const USER_PROFILES_TABLES: TableSubscription[] = [{ table: "user_profiles", event: "*" }];
const USER_FEEDBACK_TABLES: TableSubscription[] = [{ table: "user_feedback", event: "*" }];
const ACCOUNT_ACTIONS_TABLES: TableSubscription[] = [{ table: "account_actions", event: "*" }];

interface UserWithActivity {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  createdAt: string;
  roles?: {
    id: string;
    name: string;
  } | null;
  activity: {
    referralsSent: number;
    connections: number;
  };
  // Account lock status
  isLocked?: boolean;
  lockedAt?: string | null;
  lockReason?: string | null;
  lockDetails?: string | null;
  unlockRequirements?: string | null;
  lockExpiresAt?: string | null;
  requiresAdminApproval?: boolean;
  /**
   * Mirror of auth.users.email_confirmed_at, denormalised onto user_profiles by trigger so the
   * list can show verification state without an auth-API call per row.
   *
   * Three states, and the difference between the last two matters: a timestamp means confirmed,
   * `null` means genuinely unconfirmed, and `undefined` means the API did not send the field at
   * all — which is what a deploy landing before the migration looks like. Treating undefined as
   * unconfirmed would flag every user on the platform, so the UI keys on `=== null`.
   */
  trigEmailConfirmedAt?: string | null;
}

interface AdminStats {
  totalUsers: number;
  totalReferrals: number;
  totalConnections: number;
  pendingSupportMessages: number;
  pendingFeedback: number;
  pendingUnlockRequests: number;
  newUsersLastWeek: number;
  referralsLastWeek: number;
  messagesLastWeek: number;
  supportMessagesLastWeek: number;
  feedbackLastWeek: number;
}

interface Connection {
  id: string;
  userId: string;
  connectedUserId: string;
  status: string;
  connectionMethod: string | null;
  isFavorite: boolean;
  createdAt: string;
  updatedAt: string;
  user?: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    company: string | null;
  } | null;
  connectedUser?: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    company: string | null;
  } | null;
}

interface Referral {
  id: string;
  senderId: string;
  receiverId: string;
  clientName: string | null;
  clientEmail: string | null;
  clientPhone: string | null;
  jobDescription: string | null;
  status: string;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
  sender?: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    company: string | null;
  } | null;
  receiver?: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    company: string | null;
  } | null;
}

interface ChatMessage {
  id: string;
  chatId: string;
  senderId: string;
  recipientId: string;
  messageContent: string;
  createdAt: string;
  sender?: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
  } | null;
  recipient?: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
  } | null;
}

interface UserFeedback {
  id: string;
  userId: string;
  category: string;
  message: string;
  status: string;
  adminNotes: string | null;
  reviewedAt: string | null;
  createdAt: string;
  updatedAt: string;
  replies?: AdminReply[];
  user?: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
  } | null;
  reviewer?: {
    id: string;
    firstName: string | null;
    lastName: string | null;
  } | null;
}

interface UnlockRequest {
  id: string;
  userId: string;
  userEmail: string | null;
  actionType: string;
  lockReason: string | null;
  lockDetails: string | null;  // This contains the user's message
  createdAt: string;
  user?: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    isLocked: boolean;
    lockReason: string | null;
    lockDetails: string | null;
    lockedAt: string | null;
  } | null;
}

interface Industry {
  id: string;
  name: string;
  slug: string;
  created_at: string;
  updated_at: string;
}

interface MarketType {
  id: string;
  name: string;
  slug: string;
  created_at: string;
  updated_at: string;
}

interface PrimaryTrade {
  id: string;
  name: string;
  description: string | null;
  is_default: boolean;
  icon_name: string | null;
  section: string | null;
  display_order: number | null;
  is_section_header: boolean | null;
  created_at: string;
  updated_at: string;
  userCount?: number;
  totalUsage?: number;
}

// Used for the add-trade Section select only if the loaded trade list has no sections.
const FALLBACK_TRADE_SECTIONS = [
  "Core Home Services",
  "Restoration / Environmental",
  "Construction / Remodel",
  "Exterior / Building Envelope",
  "Outdoor / Site Work",
  "Systems / Specialty",
  "Other",
];

export default function AdminServicesPage() {
  const { showSuccess, showError, showInfo } = useToast();

  // Users tab → "Export CSV". Pulls every live account (not just the page or search in view) as
  // a file for email-campaign tools. The API answers with text/csv, which api.get hands back as
  // a string; a blob link is the only way to save it from a page without leaving the SPA.
  // fetch strips the UTF-8 BOM the API sends, so it is put back: without it Excel reads
  // accented names as garbage.
  const [exportingUsers, setExportingUsers] = useState(false);
  const handleExportUsers = async () => {
    setExportingUsers(true);
    try {
      const csv = await api.get<string>("/api/workflows/admin/users/export.csv");
      const blob = new Blob(["\uFEFF", csv.replace(/^\uFEFF/, "")], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `trade-loop-users-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      showSuccess("Export ready", "Your users CSV is downloading");
    } catch (error) {
      showError("Export failed", describeApiError(error));
    } finally {
      setExportingUsers(false);
    }
  };
  // The user awaiting a manual email confirmation. Held rather than acted on immediately:
  // confirming asserts we verified ownership of the address by other means, which is a claim
  // worth making deliberately (see the confirm-email route).
  const [userPendingEmailConfirm, setUserPendingEmailConfirm] = useState<UserWithActivity | null>(null);
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Only Super Admin can edit data, System Auditor has read-only access
  const canEdit = user?.isSuperAdmin === true;

  // Which user row currently has an impersonation request in flight
  const [impersonatingId, setImpersonatingId] = useState<string | null>(null);

  /**
   * Log in as this user. The server swaps the session cookie, then we hard-load
   * the app so every cached identity (React Query data, the realtime access
   * token, the Supabase Realtime socket) is rebuilt as the target user rather
   * than left holding the admin's.
   *
   * The server enforces all of this independently — this only mirrors the rules
   * so the UI doesn't offer an action that would be rejected.
   */
  const handleImpersonate = async (target: UserWithActivity) => {
    if (impersonatingId) return;
    setImpersonatingId(target.id);

    try {
      await impersonateUser(target.id);
      window.location.assign("/dashboard");
    } catch (error: any) {
      setImpersonatingId(null);
      showError(
        "Could not log in as this user",
        error?.message || "Please try again."
      );
    }
  };

  const [activeTab, setActiveTab] = useState("overview");
  const [userSearch, setUserSearch] = useState("");
  const [connectionSearch, setConnectionSearch] = useState("");
  const [referralSearch, setReferralSearch] = useState("");
  const [messageSearch, setMessageSearch] = useState("");
  const [supportStatusFilter, setSupportStatusFilter] = useState<string>("");
  const [connectionStatusFilter, setConnectionStatusFilter] = useState<string>("");
  const [referralStatusFilter, setReferralStatusFilter] = useState<string>("");
  const [userLockStatusFilter, setUserLockStatusFilter] = useState<string>("");
  const [lockedUsersSearch, setLockedUsersSearch] = useState("");
  const [feedbackSearch, setFeedbackSearch] = useState("");
  const [feedbackStatusFilter, setFeedbackStatusFilter] = useState<string>("");
  const [newIndustryName, setNewIndustryName] = useState("");
  const [newMarketTypeName, setNewMarketTypeName] = useState("");
  const [newPrimaryTradeName, setNewPrimaryTradeName] = useState("");
  const [newPrimaryTradeSection, setNewPrimaryTradeSection] = useState("Other");

  // Pagination state (20 items per page)
  const [usersPage, setUsersPage] = useState(0);
  // Off by default — see backend comment on ?includeDeleted for why tombstones
  // are hidden. Toggle exposes them for audit views.
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [connectionsPage, setConnectionsPage] = useState(0);
  const [referralsPage, setReferralsPage] = useState(0);
  const [supportPage, setSupportPage] = useState(0);
  const [feedbackPage, setFeedbackPage] = useState(0);
  const itemsPerPage = 20;

  // Debounce the user search: `userSearch` is in the query key, so without this every keystroke
  // fired a request against a service-role query over the whole user table.
  const [debouncedUserSearch, setDebouncedUserSearch] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedUserSearch(userSearch.trim()), 300);
    return () => clearTimeout(timer);
  }, [userSearch]);

  // Reset pagination when filters/search changes
  useEffect(() => {
    setUsersPage(0);
  }, [debouncedUserSearch, includeDeleted]);

  useEffect(() => {
    setConnectionsPage(0);
  }, [connectionStatusFilter, connectionSearch]);

  useEffect(() => {
    setReferralsPage(0);
  }, [referralStatusFilter, referralSearch]);

  useEffect(() => {
    setSupportPage(0);
  }, [supportStatusFilter]);

  useEffect(() => {
    setFeedbackPage(0);
  }, [feedbackStatusFilter, feedbackSearch]);

  // Delete confirmation modals
  const [deleteMarketTypeModal, setDeleteMarketTypeModal] = useState<{ isOpen: boolean; item: any | null }>({ isOpen: false, item: null });
  const [deleteIndustryModal, setDeleteIndustryModal] = useState<{ isOpen: boolean; item: any | null }>({ isOpen: false, item: null });
  const [deletePrimaryTradeModal, setDeletePrimaryTradeModal] = useState<{ isOpen: boolean; item: any | null }>({ isOpen: false, item: null });

  // Account action wizard state
  const [wizardOpen, setWizardOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserWithActivity | null>(null);

  // Referral detail modal state
  const [selectedReferral, setSelectedReferral] = useState<Referral | null>(null);
  const [referralModalOpen, setReferralModalOpen] = useState(false);

  // User permissions modal state
  const [permissionsModalOpen, setPermissionsModalOpen] = useState(false);
  const [selectedUserForPermissions, setSelectedUserForPermissions] = useState<UserWithActivity | null>(null);

  // Support message admin notes state
  const [editingNotesFor, setEditingNotesFor] = useState<string | null>(null);
  const [adminNotesInput, setAdminNotesInput] = useState<string>("");

  // Reply state for support messages and feedback
  const [replyingToSupportMessage, setReplyingToSupportMessage] = useState<string | null>(null);
  const [supportReplyInput, setSupportReplyInput] = useState<string>("");
  const [replyingToFeedback, setReplyingToFeedback] = useState<string | null>(null);
  const [feedbackReplyInput, setFeedbackReplyInput] = useState<string>("");

  // Refs for auto-scrolling reply containers
  const supportReplyRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const feedbackReplyRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Fetch admin stats - ALWAYS load (needed for overview tab)
  // Note: api.get() returns the JSON body directly (not wrapped in .data like axios)
  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: ["admin", "stats"],
    queryFn: async () => {
      const response = await api.get("/api/workflows/admin/stats");
      return response.data as AdminStats;
    },
    // Always load stats - shown in overview tab
  });

  // Fetch support messages - LAZY LOAD when support or overview tab is active
  const { data: supportData, isLoading: supportLoading } = useQuery({
    queryKey: ["admin", "support-messages", supportStatusFilter, supportPage],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (supportStatusFilter) params.append("status", supportStatusFilter);
      params.append("limit", itemsPerPage.toString());
      params.append("offset", (supportPage * itemsPerPage).toString());
      const response = await api.get(`/api/workflows/admin/support-messages?${params}`);
      return response;
    },
    enabled: activeTab === "support" || activeTab === "overview",
  });

  // Fetch users - LAZY LOAD when users or overview tab is active
  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ["admin", "users", debouncedUserSearch, usersPage, includeDeleted],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (debouncedUserSearch) params.append("search", debouncedUserSearch);
      params.append("limit", itemsPerPage.toString());
      params.append("offset", (usersPage * itemsPerPage).toString());
      if (includeDeleted) params.append("includeDeleted", "true");
      const response = await api.get(`/api/workflows/admin/users?${params}`);
      return response;
    },
    enabled: activeTab === "users" || activeTab === "overview",
  });

  // Fetch connections - LAZY LOAD when connections or overview tab is active
  const { data: connectionsData, isLoading: connectionsLoading } = useQuery({
    queryKey: ["admin", "connections", connectionStatusFilter, connectionSearch, connectionsPage],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (connectionStatusFilter) params.append("status", connectionStatusFilter);
      if (connectionSearch) params.append("search", connectionSearch);
      params.append("limit", itemsPerPage.toString());
      params.append("offset", (connectionsPage * itemsPerPage).toString());
      const response = await api.get(`/api/workflows/admin/connections?${params}`);
      return response;
    },
    enabled: activeTab === "connections" || activeTab === "overview",
  });

  // Fetch referrals - LAZY LOAD when referrals or overview tab is active
  const { data: referralsData, isLoading: referralsLoading } = useQuery({
    queryKey: ["admin", "referrals", referralStatusFilter, referralSearch, referralsPage],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (referralStatusFilter) params.append("status", referralStatusFilter);
      if (referralSearch) params.append("search", referralSearch);
      params.append("limit", itemsPerPage.toString());
      params.append("offset", (referralsPage * itemsPerPage).toString());
      const response = await api.get(`/api/workflows/admin/referrals?${params}`);
      return response;
    },
    enabled: activeTab === "referrals" || activeTab === "overview",
  });

  // Fetch messages - LAZY LOAD when messages tab is active
  const { data: messagesData, isLoading: messagesLoading } = useQuery({
    queryKey: ["admin", "messages", messageSearch],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (messageSearch) params.append("search", messageSearch);
      const response = await api.get(`/api/workflows/admin/messages?${params}`);
      return response;
    },
    enabled: activeTab === "messages",
  });

  // Fetch feedback - LAZY LOAD when feedback or overview tab is active
  const { data: feedbackData, isLoading: feedbackLoading } = useQuery({
    queryKey: ["admin", "feedback", feedbackStatusFilter, feedbackSearch, feedbackPage],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (feedbackStatusFilter) params.append("status", feedbackStatusFilter);
      if (feedbackSearch) params.append("search", feedbackSearch);
      params.append("limit", itemsPerPage.toString());
      params.append("offset", (feedbackPage * itemsPerPage).toString());
      const response = await api.get(`/api/workflows/admin/feedback?${params}`);
      return response;
    },
    enabled: activeTab === "feedback" || activeTab === "overview",
  });

  // Fetch unlock requests - LAZY LOAD when unlock-requests or overview tab is active
  const { data: unlockRequestsData, isLoading: unlockRequestsLoading } = useQuery({
    queryKey: ["admin", "unlock-requests"],
    queryFn: async () => {
      const response = await api.get(`/api/workflows/admin/unlock-requests?status=pending`);
      return response;
    },
    enabled: activeTab === "unlock-requests" || activeTab === "overview",
  });

  // Fetch industries for admin management - LAZY LOAD when lookups tab is active
  const { data: industriesData, isLoading: industriesLoading } = useQuery({
    queryKey: ["admin", "lookups", "industries"],
    queryFn: async () => {
      const response = await api.get(`/api/workflows/admin/lookups/industries`);
      return response;
    },
    enabled: activeTab === "lookups",
  });

  // Fetch market types for admin management - LAZY LOAD when lookups tab is active
  const { data: marketTypesData, isLoading: marketTypesLoading } = useQuery({
    queryKey: ["admin", "lookups", "market-types"],
    queryFn: async () => {
      const response = await api.get(`/api/workflows/admin/lookups/market-types`);
      return response;
    },
    enabled: activeTab === "lookups",
  });

  // Fetch primary trades for admin management - LAZY LOAD when lookups tab is active
  const { data: primaryTradesData, isLoading: primaryTradesLoading } = useQuery({
    queryKey: ["admin", "lookups", "primary-trades"],
    queryFn: async () => {
      const response = await api.get(`/api/workflows/admin/lookups/primary-trades`);
      return response;
    },
    enabled: activeTab === "lookups",
  });

  // Sections already in use, in display order, with 'Other' always offered (and last)
  const primaryTradeSections = useMemo(() => {
    const firstOrder = new Map<string, number>();
    for (const trade of (primaryTradesData?.data ?? []) as PrimaryTrade[]) {
      const section = trade.section?.trim();
      if (trade.is_section_header || !section || section === "Other") continue;
      firstOrder.set(section, Math.min(firstOrder.get(section) ?? Infinity, trade.display_order ?? 0));
    }
    if (firstOrder.size === 0) return FALLBACK_TRADE_SECTIONS;
    return [...[...firstOrder.keys()].sort((a, b) => firstOrder.get(a)! - firstOrder.get(b)!), "Other"];
  }, [primaryTradesData]);

  // Realtime callback for invalidating queries
  const handleRealtimeChange = useCallback(
    (table: string) => {
      switch (table) {
        case "support_messages":
          queryClient.invalidateQueries({ queryKey: ["admin", "support-messages"] });
          queryClient.invalidateQueries({ queryKey: ["admin", "stats"] });
          break;
        case "connections":
          queryClient.invalidateQueries({ queryKey: ["admin", "connections"] });
          queryClient.invalidateQueries({ queryKey: ["admin", "stats"] });
          break;
        case "referrals":
          queryClient.invalidateQueries({ queryKey: ["admin", "referrals"] });
          queryClient.invalidateQueries({ queryKey: ["admin", "stats"] });
          break;
        case "chat_messages":
          queryClient.invalidateQueries({ queryKey: ["admin", "messages"] });
          break;
        case "user_profiles":
          queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
          queryClient.invalidateQueries({ queryKey: ["admin", "stats"] });
          break;
        case "user_feedback":
          queryClient.invalidateQueries({ queryKey: ["admin", "feedback"] });
          break;
        case "account_actions":
          queryClient.invalidateQueries({ queryKey: ["admin", "unlock-requests"] });
          queryClient.invalidateQueries({ queryKey: ["admin", "stats"] });
          break;
      }
    },
    [queryClient]
  );

  // Supabase Realtime subscriptions for admin data
  useRealtimeSubscription({
    channelName: "admin-support-messages",
    enabled: activeTab === "support" || activeTab === "overview",
    tables: SUPPORT_MESSAGES_TABLES,
    onPostgresChange: (table) => handleRealtimeChange(table),
    debounceMs: 500,
  });

  useRealtimeSubscription({
    channelName: "admin-connections",
    enabled: activeTab === "connections" || activeTab === "overview",
    tables: CONNECTIONS_TABLES,
    onPostgresChange: (table) => handleRealtimeChange(table),
    debounceMs: 500,
  });

  useRealtimeSubscription({
    channelName: "admin-referrals",
    enabled: activeTab === "referrals" || activeTab === "overview",
    tables: REFERRALS_TABLES,
    onPostgresChange: (table) => handleRealtimeChange(table),
    debounceMs: 500,
  });

  useRealtimeSubscription({
    channelName: "admin-chat-messages",
    enabled: activeTab === "messages",
    tables: CHAT_MESSAGES_TABLES,
    onPostgresChange: (table) => handleRealtimeChange(table),
    debounceMs: 500,
  });

  useRealtimeSubscription({
    channelName: "admin-users",
    enabled: activeTab === "users" || activeTab === "overview",
    tables: USER_PROFILES_TABLES,
    onPostgresChange: (table) => handleRealtimeChange(table),
    debounceMs: 500,
  });

  useRealtimeSubscription({
    channelName: "admin-feedback",
    enabled: activeTab === "feedback" || activeTab === "overview",
    tables: USER_FEEDBACK_TABLES,
    onPostgresChange: (table) => handleRealtimeChange(table),
    debounceMs: 500,
  });

  useRealtimeSubscription({
    channelName: "admin-unlock-requests",
    enabled: activeTab === "unlock-requests" || activeTab === "overview",
    tables: ACCOUNT_ACTIONS_TABLES,
    onPostgresChange: (table) => handleRealtimeChange(table),
    debounceMs: 500,
  });

  // Update support message status
  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status, admin_notes }: { id: string; status: string; admin_notes?: string }) => {
      return api.put(`/api/workflows/admin/support-messages/${id}`, { status, admin_notes });
    },
    onSuccess: () => {
      showSuccess("Status Updated", "Support message status updated successfully");
      queryClient.invalidateQueries({ queryKey: ["admin", "support-messages"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "stats"] });
    },
    onError: (error: any) => {
      showError("Update Failed", error.message || "Failed to update status");
    },
  });

  // Delete support message
  const deleteSupportMessageMutation = useMutation({
    mutationFn: async (id: string) => {
      return api.delete(`/api/workflows/admin/support-messages/${id}`);
    },
    onSuccess: () => {
      showSuccess("Deleted", "Support message deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["admin", "support-messages"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "stats"] });
    },
    onError: (error: any) => {
      showError("Delete Failed", error.message || "Failed to delete support message");
    },
  });

  // Reply to support message (sends notification to user)
  const replySupportMessageMutation = useMutation({
    mutationFn: async ({ id, reply_message }: { id: string; reply_message: string }) => {
      return api.post(`/api/workflows/admin/support-messages/${id}/reply`, { reply_message });
    },
    onSuccess: async (response, variables) => {
      showSuccess("Reply Sent", "Your reply has been sent to the user as a notification");

      // Optimistically update the cache with the new reply
      const newReply = response?.data?.reply;
      if (newReply) {
        queryClient.setQueryData(
          ["admin", "support-messages", supportStatusFilter, supportPage],
          (oldData: any) => {
            if (!oldData?.data) return oldData;
            return {
              ...oldData,
              data: oldData.data.map((msg: any) => {
                if (msg.id === variables.id) {
                  return {
                    ...msg,
                    replies: [...(msg.replies || []), {
                      id: newReply.id,
                      reply: newReply.reply,
                      sentAt: newReply.sentAt || new Date().toISOString(),
                      createdAt: newReply.createdAt || new Date().toISOString(),
                      userId: newReply.userId,
                      user: {
                        firstName: user?.firstName || 'Admin',
                        lastName: user?.lastName || ''
                      }
                    }]
                  };
                }
                return msg;
              })
            };
          }
        );
      }

      queryClient.invalidateQueries({ queryKey: ["admin", "stats"] });
      setReplyingToSupportMessage(null);
      setSupportReplyInput("");

      // Auto-scroll the reply container to bottom after a brief delay for DOM update
      setTimeout(() => {
        const container = supportReplyRefs.current.get(variables.id);
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      }, 50);
    },
    onError: (error: any) => {
      showError("Reply Failed", error.message || "Failed to send reply");
    },
  });

  // Update connection status
  const updateConnectionMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      return api.put(`/api/workflows/admin/connections/${id}`, { status });
    },
    onSuccess: () => {
      showSuccess("Connection Updated", "Connection status updated successfully");
      queryClient.invalidateQueries({ queryKey: ["admin", "connections"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "stats"] });
    },
    onError: (error: any) => {
      showError("Update Failed", error.message || "Failed to update connection");
    },
  });

  // Delete connection
  const deleteConnectionMutation = useMutation({
    mutationFn: async (id: string) => {
      return api.delete(`/api/workflows/admin/connections/${id}`);
    },
    onSuccess: () => {
      showSuccess("Deleted", "Connection deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["admin", "connections"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "stats"] });
    },
    onError: (error: any) => {
      showError("Delete Failed", error.message || "Failed to delete connection");
    },
  });

  // Update referral
  const updateReferralMutation = useMutation({
    mutationFn: async ({ id, status, notes }: { id: string; status?: string; notes?: string }) => {
      return api.put(`/api/workflows/admin/referrals/${id}`, { status, notes });
    },
    onSuccess: () => {
      showSuccess("Referral Updated", "Referral updated successfully");
      queryClient.invalidateQueries({ queryKey: ["admin", "referrals"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "stats"] });
    },
    onError: (error: any) => {
      showError("Update Failed", error.message || "Failed to update referral");
    },
  });

  // Delete referral
  const deleteReferralMutation = useMutation({
    mutationFn: async (id: string) => {
      return api.delete(`/api/workflows/admin/referrals/${id}`);
    },
    onSuccess: () => {
      showSuccess("Deleted", "Referral deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["admin", "referrals"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "stats"] });
    },
    onError: (error: any) => {
      showError("Delete Failed", error.message || "Failed to delete referral");
    },
  });

  // Delete chat message
  const deleteMessageMutation = useMutation({
    mutationFn: async (id: string) => {
      return api.delete(`/api/workflows/admin/messages/${id}`);
    },
    onSuccess: () => {
      showSuccess("Deleted", "Message deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["admin", "messages"] });
    },
    onError: (error: any) => {
      showError("Delete Failed", error.message || "Failed to delete message");
    },
  });

  // Lock user account
  const lockUserMutation = useMutation({
    mutationFn: async ({ userId, data }: { userId: string; data: LockAccountData }) => {
      // Calculate expiration date based on timeframe
      let expiresAt: string | null = null;
      if (data.timeframe !== "indefinite") {
        const now = new Date();
        switch (data.timeframe) {
          case "24h":
            now.setHours(now.getHours() + 24);
            break;
          case "7d":
            now.setDate(now.getDate() + 7);
            break;
          case "30d":
            now.setDate(now.getDate() + 30);
            break;
        }
        expiresAt = now.toISOString();
      }

      return api.post(`/api/workflows/admin/users/${userId}/lock`, {
        reason: data.reason === "other" ? data.customReason : data.reason,
        details: data.details,
        unlockRequirements: data.unlockRequirements,
        expiresAt,
        requiresAdminApproval: data.requiresAdminApproval,
        emailContent: data.sendEmail ? data.emailContent : undefined,
        sendEmail: data.sendEmail,
      });
    },
    onSuccess: () => {
      showSuccess("Account Locked", "User account has been locked and their sessions invalidated");
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "stats"] });
    },
    onError: (error: any) => {
      showError("Lock Failed", error.message || "Failed to lock user account");
    },
  });

  // Unlock user account
  const unlockUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      return api.post(`/api/workflows/admin/users/${userId}/unlock`, {});
    },
    onSuccess: (response: any) => {
      showSuccess("Account Unlocked", "User account has been unlocked");
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "stats"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "unlock-requests"] });

      // Open mailto link if provided
      if (response?.data?.mailtoLink) {
        window.open(response.data.mailtoLink, '_blank');
      }
    },
    onError: (error: any) => {
      showError("Unlock Failed", error.message || "Failed to unlock user account");
    },
  });

  // Confirm a user's email address by hand.
  //
  // For a user whose confirmation email cannot reach them, this is the only way in: login
  // refuses an unconfirmed account, and every other route to confirming it runs through the
  // same email that is not arriving.
  const confirmEmailMutation = useMutation({
    mutationFn: async (userId: string) => {
      return api.post(`/api/workflows/admin/users/${userId}/confirm-email`, {});
    },
    onSuccess: () => {
      showSuccess("Email Confirmed", "This user can now sign in");
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
    onError: (error: any) => {
      // Nothing went wrong here — the address was already confirmed, which is worth saying
      // plainly rather than dressing up as a failure.
      if (error?.data?.code === 'ALREADY_CONFIRMED') {
        showInfo("Already Confirmed", "This email address is already confirmed");
        return;
      }
      showError("Confirm Failed", error.message || "Failed to confirm the email address");
    },
  });

  // Permanently delete user account
  const permanentDeleteMutation = useMutation({
    mutationFn: async ({ userId, reason }: { userId: string; reason: string }) => {
      return api.delete(`/api/workflows/admin/users/${userId}/permanent`, { reason });
    },
    onSuccess: () => {
      showSuccess("Account Deleted", "User account has been permanently deleted");
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "stats"] });
    },
    onError: (error: any) => {
      showError("Delete Failed", error.message || "Failed to permanently delete user");
    },
  });

  // Update feedback status
  const updateFeedbackMutation = useMutation({
    mutationFn: async ({ id, status, admin_notes }: { id: string; status?: string; admin_notes?: string }) => {
      return api.put(`/api/workflows/admin/feedback/${id}`, { status, admin_notes });
    },
    onSuccess: () => {
      showSuccess("Feedback Updated", "Feedback status updated successfully");
      queryClient.invalidateQueries({ queryKey: ["admin", "feedback"] });
    },
    onError: (error: any) => {
      showError("Update Failed", error.message || "Failed to update feedback");
    },
  });

  // Delete feedback
  const deleteFeedbackMutation = useMutation({
    mutationFn: async (id: string) => {
      return api.delete(`/api/workflows/admin/feedback/${id}`);
    },
    onSuccess: () => {
      showSuccess("Deleted", "Feedback deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["admin", "feedback"] });
    },
    onError: (error: any) => {
      showError("Delete Failed", error.message || "Failed to delete feedback");
    },
  });

  // Reply to feedback (sends notification to user)
  const replyFeedbackMutation = useMutation({
    mutationFn: async ({ id, reply_message }: { id: string; reply_message: string }) => {
      return api.post(`/api/workflows/admin/feedback/${id}/reply`, { reply_message });
    },
    onSuccess: async (response, variables) => {
      showSuccess("Reply Sent", "Your reply has been sent to the user as a notification");

      // Optimistically update the cache with the new reply
      const newReply = response?.data?.reply;
      if (newReply) {
        queryClient.setQueryData(
          ["admin", "feedback", feedbackStatusFilter, feedbackSearch, feedbackPage],
          (oldData: any) => {
            if (!oldData?.data) return oldData;
            return {
              ...oldData,
              data: oldData.data.map((fb: any) => {
                if (fb.id === variables.id) {
                  return {
                    ...fb,
                    replies: [...(fb.replies || []), {
                      id: newReply.id,
                      reply: newReply.reply,
                      sentAt: newReply.sentAt || new Date().toISOString(),
                      createdAt: newReply.createdAt || new Date().toISOString(),
                      userId: newReply.userId,
                      user: {
                        firstName: user?.firstName || 'Admin',
                        lastName: user?.lastName || ''
                      }
                    }]
                  };
                }
                return fb;
              })
            };
          }
        );
      }

      setReplyingToFeedback(null);
      setFeedbackReplyInput("");

      // Auto-scroll the reply container to bottom after a brief delay for DOM update
      setTimeout(() => {
        const container = feedbackReplyRefs.current.get(variables.id);
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      }, 50);
    },
    onError: (error: any) => {
      showError("Reply Failed", error.message || "Failed to send reply");
    },
  });

  // Dismiss unlock request
  const dismissUnlockRequestMutation = useMutation({
    mutationFn: async (id: string) => {
      return api.delete(`/api/workflows/admin/unlock-requests/${id}`);
    },
    onSuccess: () => {
      showSuccess("Dismissed", "Unlock request dismissed");
      queryClient.invalidateQueries({ queryKey: ["admin", "unlock-requests"] });
    },
    onError: (error: any) => {
      showError("Dismiss Failed", error.message || "Failed to dismiss unlock request");
    },
  });

  // Create industry
  const createIndustryMutation = useMutation({
    mutationFn: async (name: string) => {
      return api.post(`/api/workflows/admin/lookups/industries`, { name });
    },
    onSuccess: () => {
      showSuccess("Industry Added", "New industry added successfully");
      queryClient.invalidateQueries({ queryKey: ["admin", "lookups", "industries"] });
      queryClient.refetchQueries({ queryKey: ["industries"] }); // Force immediate background refetch
      setNewIndustryName("");
    },
    onError: (error: any) => {
      showError("Failed to Add Industry", error.message || "Failed to add industry");
    },
  });

  // Delete industry
  const deleteIndustryMutation = useMutation({
    mutationFn: async (id: string) => {
      return api.delete(`/api/workflows/admin/lookups/industries/${id}`);
    },
    onSuccess: () => {
      showSuccess("Industry Deleted", "Industry deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["admin", "lookups", "industries"] });
      queryClient.refetchQueries({ queryKey: ["industries"] }); // Force immediate background refetch
    },
    onError: (error: any) => {
      showError("Failed to Delete Industry", error.message || "Failed to delete industry");
    },
  });

  // Create market type
  const createMarketTypeMutation = useMutation({
    mutationFn: async (name: string) => {
      return api.post(`/api/workflows/admin/lookups/market-types`, { name });
    },
    onSuccess: () => {
      showSuccess("Market Type Added", "New market type added successfully");
      queryClient.invalidateQueries({ queryKey: ["admin", "lookups", "market-types"] });
      queryClient.refetchQueries({ queryKey: ["marketTypes"] }); // Force immediate background refetch
      setNewMarketTypeName("");
    },
    onError: (error: any) => {
      showError("Failed to Add Market Type", error.message || "Failed to add market type");
    },
  });

  // Delete market type
  const deleteMarketTypeMutation = useMutation({
    mutationFn: async (id: string) => {
      return api.delete(`/api/workflows/admin/lookups/market-types/${id}`);
    },
    onSuccess: () => {
      showSuccess("Market Type Deleted", "Market type deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["admin", "lookups", "market-types"] });
      queryClient.refetchQueries({ queryKey: ["marketTypes"] }); // Force immediate background refetch
    },
    onError: (error: any) => {
      showError("Failed to Delete Market Type", error.message || "Failed to delete market type");
    },
  });

  // Create primary trade
  const createPrimaryTradeMutation = useMutation({
    mutationFn: async (name: string) => {
      return api.post(`/api/workflows/admin/lookups/primary-trades`, { name, section: newPrimaryTradeSection });
    },
    onSuccess: () => {
      showSuccess("Primary Trade Added", "New primary trade added successfully");
      queryClient.invalidateQueries({ queryKey: ["admin", "lookups", "primary-trades"] });
      queryClient.refetchQueries({ queryKey: ["tradeCategories"] }); // Force immediate background refetch
      setNewPrimaryTradeName("");
    },
    onError: (error: any) => {
      showError("Failed to Add Primary Trade", error.message || "Failed to add primary trade");
    },
  });

  // Delete primary trade
  const deletePrimaryTradeMutation = useMutation({
    mutationFn: async (id: string) => {
      return api.delete(`/api/workflows/admin/lookups/primary-trades/${id}`);
    },
    onSuccess: () => {
      showSuccess("Primary Trade Deleted", "Primary trade deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["admin", "lookups", "primary-trades"] });
      queryClient.refetchQueries({ queryKey: ["tradeCategories"] }); // Force immediate background refetch
    },
    onError: (error: any) => {
      showError("Failed to Delete Primary Trade", error.message || "Failed to delete primary trade");
    },
  });

  // Helper function to convert statusId to status string for referrals
  const getStatusFromId = (statusId: number | undefined): string => {
    if (!statusId) return 'pending';
    switch (statusId) {
      case 1: return 'pending';
      case 2: return 'accepted';
      case 3: return 'completed';
      case 4: return 'declined';
      default: return 'pending';
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">Pending</Badge>;
      case "in_progress":
        return <Badge variant="secondary" className="bg-blue-100 text-blue-800">In Progress</Badge>;
      case "resolved":
        return <Badge variant="secondary" className="bg-green-100 text-green-800">Resolved</Badge>;
      case "closed":
        return <Badge variant="secondary" className="bg-gray-100 text-gray-800">Closed</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getConnectionStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">Pending</Badge>;
      case "accepted":
        return <Badge variant="secondary" className="bg-green-100 text-green-800">Connected</Badge>;
      case "blocked":
        return <Badge variant="secondary" className="bg-red-100 text-red-800">Blocked</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getReferralStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">Pending</Badge>;
      case "accepted":
        return <Badge variant="secondary" className="bg-blue-100 text-blue-800">Accepted</Badge>;
      case "declined":
        return <Badge variant="secondary" className="bg-red-100 text-red-800">Declined</Badge>;
      case "completed":
        return <Badge variant="secondary" className="bg-green-100 text-green-800">Completed</Badge>;
      case "cancelled":
        return <Badge variant="secondary" className="bg-gray-100 text-gray-800">Cancelled</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getFeedbackStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">Pending</Badge>;
      case "reviewed":
        return <Badge variant="secondary" className="bg-green-100 text-green-800">Reviewed</Badge>;
      case "archived":
        return <Badge variant="secondary" className="bg-gray-100 text-gray-800">Archived</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getCategoryBadge = (category: string) => {
    switch (category.toLowerCase()) {
      case "bug":
        return <Badge variant="secondary" className="bg-red-100 text-red-800">Bug</Badge>;
      case "feature":
        return <Badge variant="secondary" className="bg-blue-100 text-blue-800">Feature</Badge>;
      case "improvement":
        return <Badge variant="secondary" className="bg-purple-100 text-purple-800">Improvement</Badge>;
      case "general":
        return <Badge variant="secondary" className="bg-gray-100 text-gray-800">General</Badge>;
      default:
        return <Badge variant="secondary">{category}</Badge>;
    }
  };

  const getUserDisplayName = (user: { firstName?: string | null; lastName?: string | null; email?: string | null } | null | undefined) => {
    if (!user) return "Unknown";
    if (user.firstName && user.lastName) return `${user.firstName} ${user.lastName}`;
    return user.email || "Unknown";
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Open wizard for a user
  const openAccountWizard = (userData: UserWithActivity) => {
    setSelectedUser(userData);
    setWizardOpen(true);
  };

  // Handle lock account from wizard
  const handleLockAccount = async (data: LockAccountData) => {
    if (!selectedUser) return;
    await lockUserMutation.mutateAsync({ userId: selectedUser.id, data });
  };

  // Handle permanent delete from wizard
  const handleDeleteAccount = async (reason: string) => {
    if (!selectedUser) return;
    await permanentDeleteMutation.mutateAsync({ userId: selectedUser.id, reason });
  };

  // Handle unlock account
  const handleUnlockAccount = async (userId: string) => {
    await unlockUserMutation.mutateAsync(userId);
  };

  // Handle manual email confirmation — asks first, then acts on confirmEmailConfirmed.
  const handleConfirmEmail = (userData: UserWithActivity) => {
    setUserPendingEmailConfirm(userData);
  };

  const confirmEmailConfirmed = async () => {
    if (!userPendingEmailConfirm) return;
    const target = userPendingEmailConfirm;
    setUserPendingEmailConfirm(null);
    await confirmEmailMutation.mutateAsync(target.id);
  };

  return (
    <AppLayout activeTab="admin" title="Admin Services" backLink="/settings">
      <div className="max-w-4xl mx-auto p-4 pb-[80px] space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
            <BarChart3 className="w-6 h-6 text-purple-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Admin Services</h1>
            <p className="text-sm text-gray-500">System monitoring and management</p>
          </div>
        </div>

        {/* Admin sub-pages */}
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/admin/store-updates')}
          >
            Store Update Manifest
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/admin/push-diagnostics')}
          >
            Push Diagnostics
          </Button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="flex flex-wrap h-auto gap-1 p-1">
            <TabsTrigger value="overview" className="text-xs">Overview</TabsTrigger>
            <TabsTrigger value="users" className="text-xs">Users</TabsTrigger>
            <TabsTrigger value="connections" className="text-xs">Connections</TabsTrigger>
            <TabsTrigger value="referrals" className="text-xs">Referrals</TabsTrigger>
            <TabsTrigger value="messages" className="text-xs">Messages</TabsTrigger>
            <TabsTrigger value="support-feedback" className="text-xs">Support & Feedback</TabsTrigger>
            <TabsTrigger value="unlock-requests" className="text-xs">
              Unlock
              {(unlockRequestsData?.data?.length ?? 0) > 0 && (
                <Badge variant="secondary" className="ml-1 bg-yellow-100 text-yellow-800 text-xs px-1.5 py-0">
                  {unlockRequestsData?.data?.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="comps" className="text-xs">Comps</TabsTrigger>
            <TabsTrigger value="lookups" className="text-xs">
              <List className="w-3 h-3 mr-1" />
              Lookups
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4">
            {statsLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : (
              <>
                {/* Stats Grid */}
                <div className="grid grid-cols-2 gap-4">
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <Users className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-gray-900">{statsData?.totalUsers || 0}</p>
                          <p className="text-xs text-gray-500">Total Users</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                          <ArrowLeftRight className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-gray-900">{statsData?.totalReferrals || 0}</p>
                          <p className="text-xs text-gray-500">Total Referrals</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                          <Users className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-gray-900">{statsData?.totalConnections || 0}</p>
                          <p className="text-xs text-gray-500">Connections</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                          <MessageSquare className="w-5 h-5 text-yellow-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-gray-900">{statsData?.pendingSupportMessages || 0}</p>
                          <p className="text-xs text-gray-500">Pending Support</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Pending Items */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Pending Items</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between py-2 border-b">
                      <div className="flex items-center gap-3">
                        <MessageSquare className="w-5 h-5 text-yellow-500" />
                        <span className="text-sm text-gray-700">Support Messages</span>
                      </div>
                      <Badge variant="secondary" className={statsData?.pendingSupportMessages ? "bg-yellow-100 text-yellow-800" : "bg-gray-100 text-gray-600"}>
                        {statsData?.pendingSupportMessages || 0}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b">
                      <div className="flex items-center gap-3">
                        <MessageCircle className="w-5 h-5 text-indigo-500" />
                        <span className="text-sm text-gray-700">User Feedback</span>
                      </div>
                      <Badge variant="secondary" className={statsData?.pendingFeedback ? "bg-indigo-100 text-indigo-800" : "bg-gray-100 text-gray-600"}>
                        {statsData?.pendingFeedback || 0}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <div className="flex items-center gap-3">
                        <Unlock className="w-5 h-5 text-orange-500" />
                        <span className="text-sm text-gray-700">Unlock Requests</span>
                      </div>
                      <Badge variant="secondary" className={statsData?.pendingUnlockRequests ? "bg-orange-100 text-orange-800" : "bg-gray-100 text-gray-600"}>
                        {statsData?.pendingUnlockRequests || 0}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>

                {/* This Week Stats */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">This Week</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between py-2 border-b">
                      <div className="flex items-center gap-3">
                        <UserPlus className="w-5 h-5 text-blue-500" />
                        <span className="text-sm text-gray-700">New Users</span>
                      </div>
                      <span className="font-semibold text-gray-900">+{statsData?.newUsersLastWeek || 0}</span>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b">
                      <div className="flex items-center gap-3">
                        <ArrowLeftRight className="w-5 h-5 text-green-500" />
                        <span className="text-sm text-gray-700">New Referrals</span>
                      </div>
                      <span className="font-semibold text-gray-900">+{statsData?.referralsLastWeek || 0}</span>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b">
                      <div className="flex items-center gap-3">
                        <MessageSquare className="w-5 h-5 text-purple-500" />
                        <span className="text-sm text-gray-700">New Messages</span>
                      </div>
                      <span className="font-semibold text-gray-900">+{statsData?.messagesLastWeek || 0}</span>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b">
                      <div className="flex items-center gap-3">
                        <Mail className="w-5 h-5 text-yellow-500" />
                        <span className="text-sm text-gray-700">Support Messages</span>
                      </div>
                      <span className="font-semibold text-gray-900">+{statsData?.supportMessagesLastWeek || 0}</span>
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <div className="flex items-center gap-3">
                        <MessageCircle className="w-5 h-5 text-orange-500" />
                        <span className="text-sm text-gray-700">User Feedback</span>
                      </div>
                      <span className="font-semibold text-gray-900">+{statsData?.feedbackLastWeek || 0}</span>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-4">
            {/* Search and Filter */}
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search by name, email or company..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="whitespace-nowrap">
                    <Filter className="w-4 h-4 mr-2" />
                    {userLockStatusFilter === "locked" ? "Locked Only" : userLockStatusFilter === "unlocked" ? "Unlocked Only" : "All Users"}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setUserLockStatusFilter("")}>
                    All Users
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setUserLockStatusFilter("locked")}>
                    Locked Only
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setUserLockStatusFilter("unlocked")}>
                    Unlocked Only
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                variant={includeDeleted ? "default" : "outline"}
                size="sm"
                onClick={() => setIncludeDeleted((prev) => !prev)}
                className="whitespace-nowrap"
                title="Toggle whether deleted-account tombstones appear in the list"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {includeDeleted ? "Hide Deleted" : "Include Deleted"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportUsers}
                disabled={exportingUsers}
                className="whitespace-nowrap"
                title="Download every active user's name, email, phone, company, trade and signup date as a CSV"
              >
                {exportingUsers ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Export CSV
              </Button>
            </div>

            {/* Users List */}
            {usersLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : (
              <div className="space-y-3">
                {usersData?.data
                  ?.filter((userData: UserWithActivity) => {
                    // Apply lock status filter
                    if (userLockStatusFilter === "locked" && !userData.isLocked) return false;
                    if (userLockStatusFilter === "unlocked" && userData.isLocked) return false;
                    return true;
                  })
                  ?.map((userData: UserWithActivity) => (
                  <Card key={userData.id} className={userData.isLocked ? "border-yellow-300 bg-yellow-50/30" : ""}>
                    <CardContent className="p-4">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                        {/* Left side: User info */}
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                            userData.isLocked ? "bg-yellow-100" : "bg-gray-100"
                          }`}>
                            {userData.isLocked ? (
                              <Lock className="w-5 h-5 text-yellow-600" />
                            ) : (
                              <span className="text-sm font-semibold text-gray-600">
                                {userData.firstName?.[0] || userData.email?.[0] || "U"}
                              </span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-medium text-gray-900 truncate">
                                {userData.deletedAt
                                  ? (userData.fullName || "Former Trade Loop User")
                                  : userData.firstName && userData.lastName
                                    ? highlightSearchTerms(`${userData.firstName} ${userData.lastName}`, debouncedUserSearch)
                                    : highlightSearchTerms(userData.email || "Unknown User", debouncedUserSearch)}
                              </p>
                              {userData.isLocked && (
                                <Badge variant="secondary" className="bg-yellow-100 text-yellow-800 text-xs">
                                  <Lock className="w-3 h-3 mr-1" />
                                  Locked
                                </Badge>
                              )}
                              {userData.trigEmailConfirmedAt === null && (
                                <Badge variant="secondary" className="bg-red-100 text-red-800 text-xs">
                                  <MailCheck className="w-3 h-3 mr-1" />
                                  Unverified
                                </Badge>
                              )}
                              {userData.deletedAt && (
                                <Badge variant="secondary" className="bg-gray-200 text-gray-700 text-xs">
                                  <Trash2 className="w-3 h-3 mr-1" />
                                  Deleted
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-gray-500 truncate">
                              {userData.deletedAt
                                ? (userData.deletedCompanyName
                                    ? highlightSearchTerms(userData.deletedCompanyName, debouncedUserSearch)
                                    : <span className="italic">no company on record</span>)
                                : highlightSearchTerms(userData.email, debouncedUserSearch)}
                            </p>
                            <div className="flex items-center gap-3 mt-1 text-xs text-gray-400 flex-wrap">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                Joined {formatDate(userData.createdAt)}
                              </span>
                              {userData.deletedAt && (
                                <span className="flex items-center gap-1">
                                  <Trash2 className="w-3 h-3" />
                                  Deleted {formatDate(userData.deletedAt)}
                                </span>
                              )}
                              {userData.roles && (
                                <Badge variant="outline" className="text-xs">
                                  {userData.roles.name}
                                </Badge>
                              )}
                            </div>
                            {/* Show lock details if locked */}
                            {userData.isLocked && (
                              <div className="mt-2 p-2 bg-yellow-100 rounded text-xs space-y-1">
                                {userData.lockReason && (
                                  <p className="text-yellow-800">
                                    <span className="font-medium">Reason:</span> {userData.lockReason}
                                  </p>
                                )}
                                {userData.lockExpiresAt && (
                                  <p className="text-yellow-700">
                                    <span className="font-medium">Expires:</span> {formatDate(userData.lockExpiresAt)}
                                  </p>
                                )}
                                {userData.requiresAdminApproval && (
                                  <p className="text-yellow-800 font-medium flex items-center gap-1">
                                    <AlertCircle className="w-3 h-3" />
                                    Requires admin approval to unlock
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Right side: Actions and Stats */}
                        <div className="flex items-center justify-between sm:flex-col sm:items-end gap-3 sm:gap-2">
                          <div className="flex items-center gap-1">
                          {/* Log in as this user. Hidden for admins (an admin
                              acting as another admin would launder audit trails)
                              and for locked accounts (their session is force-
                              rejected, which would strand the impersonator). The
                              server rejects both cases regardless. */}
                          {canEdit &&
                            userData.id !== user?.id &&
                            !userData.isLocked &&
                            !ADMIN_ROLE_NAMES.includes(
                              (userData.roles?.name || "").toLowerCase()
                            ) && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleImpersonate(userData)}
                                disabled={impersonatingId !== null}
                                title={`Log in as ${userData.firstName || userData.email || "this user"}`}
                                className="h-8 w-8 p-0 text-gray-500 hover:text-blue-700 hover:bg-blue-50"
                              >
                                {impersonatingId === userData.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <UserCog className="w-4 h-4" />
                                )}
                                <span className="sr-only">
                                  Log in as {userData.firstName || userData.email || "this user"}
                                </span>
                              </Button>
                            )}

                          {/* Actions - Hamburger menu */}
                          {canEdit && userData.id !== user?.id && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-8 w-8 p-0 text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                                >
                                  <MoreVertical className="w-4 h-4" />
                                  <span className="sr-only">Open menu</span>
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-48">
                                <DropdownMenuItem
                                  onClick={() => {
                                    setSelectedUserForPermissions(userData);
                                    setPermissionsModalOpen(true);
                                  }}
                                  className="cursor-pointer"
                                >
                                  <Shield className="w-4 h-4 mr-2 text-blue-600" />
                                  <span>Permissions</span>
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                {/* Shown only where it can do something. trigEmailConfirmedAt is
                                    mirrored onto user_profiles by trigger, so this costs no extra
                                    lookup — see the add_trig_email_confirmed_at migration. */}
                                {userData.trigEmailConfirmedAt === null && (
                                  <>
                                    <DropdownMenuItem
                                      onClick={() => handleConfirmEmail(userData)}
                                      disabled={confirmEmailMutation.isPending}
                                      className="cursor-pointer"
                                    >
                                      <MailCheck className="w-4 h-4 mr-2 text-green-600" />
                                      <span>Confirm Email</span>
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                  </>
                                )}
                                {userData.isLocked ? (
                                  <>
                                    <DropdownMenuItem
                                      onClick={() => handleUnlockAccount(userData.id)}
                                      disabled={unlockUserMutation.isPending}
                                      className="cursor-pointer"
                                    >
                                      <Unlock className="w-4 h-4 mr-2 text-green-600" />
                                      <span>Unlock Account</span>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => openAccountWizard(userData)}
                                      className="cursor-pointer"
                                    >
                                      <Lock className="w-4 h-4 mr-2 text-gray-600" />
                                      <span>Manage Account</span>
                                    </DropdownMenuItem>
                                  </>
                                ) : (
                                  <DropdownMenuItem
                                    onClick={() => openAccountWizard(userData)}
                                    disabled={lockUserMutation.isPending || permanentDeleteMutation.isPending}
                                    className="cursor-pointer"
                                  >
                                    <Lock className="w-4 h-4 mr-2 text-yellow-600" />
                                    <span>Lock/Manage Account</span>
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                          </div>

                          {/* Stats - Always visible, right-aligned on desktop */}
                          <div className="flex items-center gap-4 text-sm">
                            <div className="text-center">
                              <p className="font-semibold text-gray-900">{userData.activity.referralsSent}</p>
                              <p className="text-xs text-gray-500">Referrals</p>
                            </div>
                            <div className="text-center">
                              <p className="font-semibold text-gray-900">{userData.activity.connections}</p>
                              <p className="text-xs text-gray-500">Partners</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {usersData?.data?.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    No users found
                  </div>
                )}
              </div>
            )}

            {/* Pagination Controls */}
            {!usersLoading && usersData?.pagination && (
              <div className="flex items-center justify-between mt-4 px-2">
                <div className="text-sm text-gray-500">
                  Showing {usersPage * itemsPerPage + 1} to {Math.min((usersPage + 1) * itemsPerPage, usersData.pagination.total)} of {usersData.pagination.total}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setUsersPage(p => Math.max(0, p - 1))}
                    disabled={usersPage === 0}
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setUsersPage(p => p + 1)}
                    disabled={(usersPage + 1) * itemsPerPage >= (usersData.pagination.total || 0)}
                  >
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Connections Tab */}
          <TabsContent value="connections" className="space-y-4">
            {/* Search Input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by user name..."
                value={connectionSearch}
                onChange={(e) => setConnectionSearch(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Status Filter */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Filter className="w-4 h-4 mr-2" />
                  Status: {connectionStatusFilter === "" ? "All" :
                           connectionStatusFilter === "pending" ? "Pending" :
                           connectionStatusFilter === "accepted" ? "Connected" :
                           connectionStatusFilter === "blocked" ? "Blocked" : "All"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem onClick={() => setConnectionStatusFilter("")}>
                  All Statuses
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setConnectionStatusFilter("pending")}>
                  <Clock className="w-4 h-4 mr-2" />
                  Pending
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setConnectionStatusFilter("accepted")}>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Connected
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setConnectionStatusFilter("blocked")}>
                  <Ban className="w-4 h-4 mr-2" />
                  Blocked
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Connections List */}
            {connectionsLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : (
              <div className="space-y-3">
                {connectionsData?.data?.map((conn: Connection) => (
                  <Card key={conn.id}>
                    <CardContent className="p-4">
                      <div className="space-y-3">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <Link2 className="w-5 h-5 text-purple-500" />
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-gray-900">
                                  {getUserDisplayName(conn.user)}
                                </p>
                                <ArrowLeftRight className="w-4 h-4 text-gray-400" />
                                <p className="font-medium text-gray-900">
                                  {getUserDisplayName(conn.connectedUser)}
                                </p>
                              </div>
                              <div className="flex items-center gap-2 mt-1">
                                {getConnectionStatusBadge(conn.status)}
                                {conn.connectionMethod && (
                                  <span className="text-xs text-gray-400">via {conn.connectionMethod}</span>
                                )}
                              </div>
                            </div>
                          </div>
                          <span className="text-xs text-gray-400">{formatDate(conn.createdAt)}</span>
                        </div>

                        {/* Edit Actions */}
                        {canEdit && (
                          <div className="flex gap-2 pt-2 border-t">
                            {conn.status !== "accepted" && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-green-600 border-green-200 hover:bg-green-50"
                                onClick={() => updateConnectionMutation.mutate({ id: conn.id, status: "accepted" })}
                                disabled={updateConnectionMutation.isPending}
                              >
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Accept
                              </Button>
                            )}
                            {conn.status !== "blocked" && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-orange-600 border-orange-200 hover:bg-orange-50"
                                onClick={() => updateConnectionMutation.mutate({ id: conn.id, status: "blocked" })}
                                disabled={updateConnectionMutation.isPending}
                              >
                                <Ban className="w-3 h-3 mr-1" />
                                Block
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 border-red-200 hover:bg-red-50"
                              onClick={() => {
                                if (confirm("Are you sure you want to delete this connection?")) {
                                  deleteConnectionMutation.mutate(conn.id);
                                }
                              }}
                              disabled={deleteConnectionMutation.isPending}
                            >
                              <Trash2 className="w-3 h-3 mr-1" />
                              Delete
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {connectionsData?.data?.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    No connections found
                  </div>
                )}
              </div>
            )}

            {/* Pagination Controls */}
            {!connectionsLoading && connectionsData?.pagination && (
              <div className="flex items-center justify-between mt-4 px-2">
                <div className="text-sm text-gray-500">
                  Showing {connectionsPage * itemsPerPage + 1} to {Math.min((connectionsPage + 1) * itemsPerPage, connectionsData.pagination.total)} of {connectionsData.pagination.total}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setConnectionsPage(p => Math.max(0, p - 1))}
                    disabled={connectionsPage === 0}
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setConnectionsPage(p => p + 1)}
                    disabled={(connectionsPage + 1) * itemsPerPage >= (connectionsData.pagination.total || 0)}
                  >
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Referrals Tab */}
          <TabsContent value="referrals" className="space-y-4">
            {/* Search Input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by user name or client name..."
                value={referralSearch}
                onChange={(e) => setReferralSearch(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Status Filter */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Filter className="w-4 h-4 mr-2" />
                  Status: {referralStatusFilter === "" ? "All" :
                           referralStatusFilter === "pending" ? "Pending" :
                           referralStatusFilter === "accepted" ? "Accepted" :
                           referralStatusFilter === "completed" ? "Completed" :
                           referralStatusFilter === "cancelled" ? "Cancelled" :
                           referralStatusFilter === "declined" ? "Declined" : "All"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem onClick={() => setReferralStatusFilter("")}>
                  All Statuses
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setReferralStatusFilter("pending")}>
                  <Clock className="w-4 h-4 mr-2" />
                  Pending
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setReferralStatusFilter("accepted")}>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Accepted
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setReferralStatusFilter("completed")}>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Completed
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setReferralStatusFilter("declined")}>
                  <XCircle className="w-4 h-4 mr-2" />
                  Declined
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setReferralStatusFilter("cancelled")}>
                  <XCircle className="w-4 h-4 mr-2" />
                  Cancelled
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Referrals List */}
            {referralsLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : (
              <div className="space-y-3">
                {referralsData?.data?.map((ref: Referral) => (
                  <Card key={ref.id}>
                    <CardContent className="p-4">
                      <div className="space-y-3">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <Send className="w-4 h-4 text-blue-500" />
                              <p className="font-medium text-gray-900">
                                {getUserDisplayName(ref.sender)} → {getUserDisplayName(ref.receiver)}
                              </p>
                              {getReferralStatusBadge(ref.status)}
                            </div>
                            {ref.clientName && (
                              <p className="text-sm text-gray-600 mt-1">
                                Client: {ref.clientName} {ref.clientEmail && `(${ref.clientEmail})`}
                              </p>
                            )}
                            {ref.jobDescription && (
                              <p className="text-sm text-gray-500 mt-1 line-clamp-2">{ref.jobDescription}</p>
                            )}
                          </div>
                          <span className="text-xs text-gray-400">{formatDate(ref.createdAt)}</span>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2 pt-2 border-t">
                          {/* View Details - Always visible */}
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-gray-600 border-gray-200 hover:bg-gray-50"
                            onClick={() => {
                              setSelectedReferral(ref);
                              setReferralModalOpen(true);
                            }}
                          >
                            <FileText className="w-3 h-3 mr-1" />
                            View Details
                          </Button>

                          {/* Edit Actions - Admin only */}
                          {canEdit && (
                            <>
                              {ref.status === "pending" && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-blue-600 border-blue-200 hover:bg-blue-50"
                                    onClick={() => updateReferralMutation.mutate({ id: ref.id, status: "accepted" })}
                                    disabled={updateReferralMutation.isPending}
                                  >
                                    Accept
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-orange-600 border-orange-200 hover:bg-orange-50"
                                    onClick={() => updateReferralMutation.mutate({ id: ref.id, status: "declined" })}
                                    disabled={updateReferralMutation.isPending}
                                  >
                                    Decline
                                  </Button>
                                </>
                              )}
                              {ref.status === "accepted" && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-green-600 border-green-200 hover:bg-green-50"
                                  onClick={() => updateReferralMutation.mutate({ id: ref.id, status: "completed" })}
                                  disabled={updateReferralMutation.isPending}
                                >
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Mark Complete
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 border-red-200 hover:bg-red-50"
                                onClick={() => {
                                  if (confirm("Are you sure you want to delete this referral?")) {
                                    deleteReferralMutation.mutate(ref.id);
                                  }
                                }}
                                disabled={deleteReferralMutation.isPending}
                              >
                                <Trash2 className="w-3 h-3 mr-1" />
                                Delete
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {referralsData?.data?.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    No referrals found
                  </div>
                )}
              </div>
            )}

            {/* Pagination Controls */}
            {!referralsLoading && referralsData?.pagination && (
              <div className="flex items-center justify-between mt-4 px-2">
                <div className="text-sm text-gray-500">
                  Showing {referralsPage * itemsPerPage + 1} to {Math.min((referralsPage + 1) * itemsPerPage, referralsData.pagination.total)} of {referralsData.pagination.total}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setReferralsPage(p => Math.max(0, p - 1))}
                    disabled={referralsPage === 0}
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setReferralsPage(p => p + 1)}
                    disabled={(referralsPage + 1) * itemsPerPage >= (referralsData.pagination.total || 0)}
                  >
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages" className="space-y-4">
            {/* Search Input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by user name or message content..."
                value={messageSearch}
                onChange={(e) => setMessageSearch(e.target.value)}
                className="pl-10"
              />
            </div>

            <div className="flex items-center gap-2 text-sm text-gray-500 bg-gray-50 p-3 rounded-lg">
              <MessageSquare className="w-4 h-4" />
              <span>Chat messages between users</span>
            </div>

            {/* Messages List */}
            {messagesLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : (
              <div className="space-y-3">
                {messagesData?.data?.map((msg: ChatMessage) => (
                  <Card key={msg.id}>
                    <CardContent className="p-4">
                      <div className="space-y-2">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-2">
                            <MessageSquare className="w-4 h-4 text-blue-500" />
                            <p className="text-sm font-medium text-gray-900">
                              {getUserDisplayName(msg.sender)} → {getUserDisplayName(msg.recipient)}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-400">{formatDate(msg.createdAt)}</span>
                            {canEdit && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                onClick={() => {
                                  if (confirm("Are you sure you want to delete this message?")) {
                                    deleteMessageMutation.mutate(msg.id);
                                  }
                                }}
                                disabled={deleteMessageMutation.isPending}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </div>
                        <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded">
                          {msg.messageContent}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {messagesData?.data?.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    No messages found
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          {/* Support Tab */}
          {/* Support & Feedback Tab */}
          <TabsContent value="support-feedback" className="space-y-6">
            {/* Support Messages Section */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Mail className="w-4 h-4 text-blue-600" />
                  </div>
                  Support Messages
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Status Filter */}
                <div className="flex gap-2 overflow-x-auto pb-2">
                  <Button
                    variant={supportStatusFilter === "" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSupportStatusFilter("")}
                  >
                    All
                  </Button>
                  <Button
                    variant={supportStatusFilter === "pending" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSupportStatusFilter("pending")}
                  >
                    <Clock className="w-3 h-3 mr-1" />
                    Pending
                  </Button>
                  <Button
                    variant={supportStatusFilter === "in_progress" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSupportStatusFilter("in_progress")}
                  >
                    <AlertCircle className="w-3 h-3 mr-1" />
                    In Progress
                  </Button>
                  <Button
                    variant={supportStatusFilter === "resolved" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSupportStatusFilter("resolved")}
                  >
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Resolved
                  </Button>
                </div>

                {/* Support Messages List */}
                {supportLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
                  </div>
                ) : (
                  <div className="space-y-3">
                    {supportData?.data?.map((msg: SupportMessage) => (
                      <div key={msg.id} className="border rounded-lg p-4 bg-gray-50">
                        <div className="space-y-3">
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-gray-900">{msg.subject}</p>
                                {getStatusBadge(msg.status)}
                              </div>
                              <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
                                <Mail className="w-3 h-3" />
                                <span>{msg.name} ({msg.email})</span>
                              </div>
                            </div>
                            <span className="text-xs text-gray-400">{formatDate(msg.createdAt)}</span>
                          </div>

                          <p className="text-sm text-gray-600 bg-white p-3 rounded-lg">
                            {msg.message}
                          </p>

                          {msg.adminNotes && !editingNotesFor && (
                            <div className="text-sm bg-blue-50 p-3 rounded-lg">
                              <p className="font-medium text-blue-800 mb-1">Admin Notes:</p>
                              <p className="text-blue-700">{msg.adminNotes}</p>
                            </div>
                          )}

                          {/* Reply Thread */}
                          {msg.replies && msg.replies.length > 0 && (
                            <div className="mt-3 space-y-2">
                              <p className="text-sm font-medium text-gray-700">Reply Thread ({msg.replies.length}):</p>
                              <div
                                ref={(el) => {
                                  if (el) supportReplyRefs.current.set(msg.id, el);
                                }}
                                className="space-y-2 max-h-60 overflow-y-auto"
                              >
                                {msg.replies.map((reply) => (
                                  <div key={reply.id} className="text-sm bg-purple-50 p-3 rounded-lg border-l-4 border-purple-400">
                                    <div className="flex justify-between items-start mb-1">
                                      <span className="font-medium text-purple-800">
                                        {reply.user ? `${reply.user.firstName || ''} ${reply.user.lastName || ''}`.trim() || 'Admin' : 'Admin'}
                                      </span>
                                      <span className="text-xs text-purple-500">{formatDate(reply.sentAt)}</span>
                                    </div>
                                    <p className="text-purple-700">{reply.reply}</p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Admin Notes Editor */}
                          {canEdit && editingNotesFor === msg.id && (
                            <div className="space-y-2">
                              <label className="text-sm font-medium text-gray-700">Admin Notes (Internal Only)</label>
                              <Input
                                value={adminNotesInput}
                                onChange={(e) => setAdminNotesInput(e.target.value)}
                                placeholder="Add notes visible only to admins..."
                                className="w-full"
                              />
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-green-600 border-green-200 hover:bg-green-50"
                                  onClick={() => {
                                    updateStatusMutation.mutate({
                                      id: msg.id,
                                      status: msg.status,
                                      admin_notes: adminNotesInput
                                    });
                                    setEditingNotesFor(null);
                                    setAdminNotesInput("");
                                  }}
                                  disabled={updateStatusMutation.isPending}
                                >
                                  Save Notes
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setEditingNotesFor(null);
                                    setAdminNotesInput("");
                                  }}
                                >
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          )}

                          {/* Reply to User Section */}
                          {canEdit && replyingToSupportMessage === msg.id && (
                            <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
                              <p className="text-sm font-medium text-blue-700 mb-2">Reply to User</p>
                              <p className="text-xs text-blue-600 mb-2">This message will be sent as a notification to the user.</p>
                              <textarea
                                className="w-full p-2 border border-blue-200 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                rows={3}
                                placeholder="Type your reply message..."
                                value={supportReplyInput}
                                onChange={(e) => setSupportReplyInput(e.target.value)}
                              />
                              <div className="flex gap-2 mt-2">
                                <Button
                                  size="sm"
                                  onClick={() => {
                                    if (supportReplyInput.trim()) {
                                      replySupportMessageMutation.mutate({ id: msg.id, reply_message: supportReplyInput.trim() });
                                    }
                                  }}
                                  disabled={!supportReplyInput.trim() || replySupportMessageMutation.isPending}
                                >
                                  {replySupportMessageMutation.isPending ? "Sending..." : "Send Reply"}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setReplyingToSupportMessage(null);
                                    setSupportReplyInput("");
                                  }}
                                >
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          )}

                          {/* Status Actions - Only Super Admin can modify */}
                          {canEdit && (
                            <div className="flex gap-2 pt-2 flex-wrap">
                              {/* Reply to User Button */}
                              {replyingToSupportMessage !== msg.id && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-purple-600 border-purple-200 hover:bg-purple-50"
                                  onClick={() => {
                                    setReplyingToSupportMessage(msg.id);
                                    setSupportReplyInput("");
                                  }}
                                >
                                  <MessageSquare className="w-3 h-3 mr-1" />
                                  Reply to User
                                </Button>
                              )}

                              {/* Admin Notes Button */}
                              {editingNotesFor !== msg.id && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-blue-600 border-blue-200 hover:bg-blue-50"
                                  onClick={() => {
                                    setEditingNotesFor(msg.id);
                                    setAdminNotesInput(msg.adminNotes || "");
                                  }}
                                >
                                  <FileText className="w-3 h-3 mr-1" />
                                  {msg.adminNotes ? "Edit Notes" : "Add Notes"}
                                </Button>
                              )}

                              {msg.status === "pending" && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => updateStatusMutation.mutate({ id: msg.id, status: "in_progress" })}
                                  disabled={updateStatusMutation.isPending}
                                >
                                  Mark In Progress
                                </Button>
                              )}
                              {(msg.status === "pending" || msg.status === "in_progress") && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-green-600 border-green-200 hover:bg-green-50"
                                  onClick={() => updateStatusMutation.mutate({ id: msg.id, status: "resolved" })}
                                  disabled={updateStatusMutation.isPending}
                                >
                                  Mark Resolved
                                </Button>
                              )}
                              {msg.status !== "closed" && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-gray-600"
                                  onClick={() => updateStatusMutation.mutate({ id: msg.id, status: "closed" })}
                                  disabled={updateStatusMutation.isPending}
                                >
                                  Close
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 border-red-200 hover:bg-red-50"
                                onClick={() => {
                                  if (confirm("Are you sure you want to delete this support message?")) {
                                    deleteSupportMessageMutation.mutate(msg.id);
                                  }
                                }}
                                disabled={deleteSupportMessageMutation.isPending}
                              >
                                <Trash2 className="w-3 h-3 mr-1" />
                                Delete
                              </Button>
                            </div>
                          )}

                          {msg.respondedAt && msg.responder && (
                            <p className="text-xs text-gray-400">
                              Responded by {msg.responder.firstName} {msg.responder.lastName} on {formatDate(msg.respondedAt)}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                    {supportData?.data?.length === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        No support messages found
                      </div>
                    )}
                  </div>
                )}

                {/* Pagination Controls */}
                {!supportLoading && supportData?.pagination && (
                  <div className="flex items-center justify-between mt-4 px-2 pt-4 border-t">
                    <div className="text-sm text-gray-500">
                      Showing {supportPage * itemsPerPage + 1} to {Math.min((supportPage + 1) * itemsPerPage, supportData.pagination.total)} of {supportData.pagination.total}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSupportPage(p => Math.max(0, p - 1))}
                        disabled={supportPage === 0}
                      >
                        <ChevronLeft className="w-4 h-4 mr-1" />
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSupportPage(p => p + 1)}
                        disabled={(supportPage + 1) * itemsPerPage >= (supportData.pagination.total || 0)}
                      >
                        Next
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* User Feedback Section */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <MessageCircle className="w-4 h-4 text-indigo-600" />
                  </div>
                  User Feedback
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
            {/* Search Input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by user name, message, or category..."
                value={feedbackSearch}
                onChange={(e) => setFeedbackSearch(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Status Filter */}
            <div className="flex gap-2 overflow-x-auto pb-2">
              <Button
                variant={feedbackStatusFilter === "" ? "default" : "outline"}
                size="sm"
                onClick={() => setFeedbackStatusFilter("")}
              >
                All
              </Button>
              <Button
                variant={feedbackStatusFilter === "pending" ? "default" : "outline"}
                size="sm"
                onClick={() => setFeedbackStatusFilter("pending")}
              >
                <Clock className="w-3 h-3 mr-1" />
                Pending
              </Button>
              <Button
                variant={feedbackStatusFilter === "reviewed" ? "default" : "outline"}
                size="sm"
                onClick={() => setFeedbackStatusFilter("reviewed")}
              >
                <CheckCircle className="w-3 h-3 mr-1" />
                Reviewed
              </Button>
              <Button
                variant={feedbackStatusFilter === "archived" ? "default" : "outline"}
                size="sm"
                onClick={() => setFeedbackStatusFilter("archived")}
              >
                <Archive className="w-3 h-3 mr-1" />
                Archived
              </Button>
            </div>

            <div className="flex items-center gap-2 text-sm text-gray-500 bg-gray-50 p-3 rounded-lg">
              <MessageCircle className="w-4 h-4" />
              <span>User feedback submitted from Settings page</span>
            </div>

            {/* Feedback List */}
            {feedbackLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : (
              <div className="space-y-3">
                {feedbackData?.data?.map((fb: UserFeedback) => (
                  <div key={fb.id} className="border rounded-lg p-4 bg-gray-50">
                    <div className="space-y-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <MessageCircle className="w-4 h-4 text-indigo-500" />
                            <p className="font-medium text-gray-900">
                              {getUserDisplayName(fb.user)}
                            </p>
                            {getCategoryBadge(fb.category)}
                            {getFeedbackStatusBadge(fb.status)}
                          </div>
                          {fb.user?.email && (
                            <p className="text-sm text-gray-500 mt-1">{fb.user.email}</p>
                          )}
                        </div>
                        <span className="text-xs text-gray-400">{formatDate(fb.createdAt)}</span>
                      </div>

                      <p className="text-sm text-gray-600 bg-white p-3 rounded-lg">
                        {fb.message}
                      </p>

                      {fb.adminNotes && (
                        <div className="text-sm bg-indigo-50 p-3 rounded-lg">
                          <p className="font-medium text-indigo-800 mb-1">Admin Notes:</p>
                          <p className="text-indigo-700">{fb.adminNotes}</p>
                        </div>
                      )}

                      {/* Reply Thread */}
                      {fb.replies && fb.replies.length > 0 && (
                        <div className="mt-3 space-y-2">
                          <p className="text-sm font-medium text-gray-700">Reply Thread ({fb.replies.length}):</p>
                          <div
                            ref={(el) => {
                              if (el) feedbackReplyRefs.current.set(fb.id, el);
                            }}
                            className="space-y-2 max-h-60 overflow-y-auto"
                          >
                            {fb.replies.map((reply) => (
                              <div key={reply.id} className="text-sm bg-purple-50 p-3 rounded-lg border-l-4 border-purple-400">
                                <div className="flex justify-between items-start mb-1">
                                  <span className="font-medium text-purple-800">
                                    {reply.user ? `${reply.user.firstName || ''} ${reply.user.lastName || ''}`.trim() || 'Admin' : 'Admin'}
                                  </span>
                                  <span className="text-xs text-purple-500">{formatDate(reply.sentAt)}</span>
                                </div>
                                <p className="text-purple-700">{reply.reply}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Reply to User Section */}
                      {canEdit && replyingToFeedback === fb.id && (
                        <div className="mt-3 p-3 bg-purple-50 rounded-lg border border-purple-100">
                          <p className="text-sm font-medium text-purple-700 mb-2">Reply to User</p>
                          <p className="text-xs text-purple-600 mb-2">This message will be sent as a notification to the user.</p>
                          <textarea
                            className="w-full p-2 border border-purple-200 rounded-md text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                            rows={3}
                            placeholder="Type your reply message..."
                            value={feedbackReplyInput}
                            onChange={(e) => setFeedbackReplyInput(e.target.value)}
                          />
                          <div className="flex gap-2 mt-2">
                            <Button
                              size="sm"
                              onClick={() => {
                                if (feedbackReplyInput.trim()) {
                                  replyFeedbackMutation.mutate({ id: fb.id, reply_message: feedbackReplyInput.trim() });
                                }
                              }}
                              disabled={!feedbackReplyInput.trim() || replyFeedbackMutation.isPending}
                            >
                              {replyFeedbackMutation.isPending ? "Sending..." : "Send Reply"}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setReplyingToFeedback(null);
                                setFeedbackReplyInput("");
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      )}

                      {/* Status Actions - Only Super Admin can modify */}
                      {canEdit && (
                        <div className="flex gap-2 pt-2 border-t flex-wrap">
                          {/* Reply to User Button */}
                          {replyingToFeedback !== fb.id && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-purple-600 border-purple-200 hover:bg-purple-50"
                              onClick={() => {
                                setReplyingToFeedback(fb.id);
                                setFeedbackReplyInput("");
                              }}
                            >
                              <MessageSquare className="w-3 h-3 mr-1" />
                              Reply to User
                            </Button>
                          )}

                          {fb.status === "pending" && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-green-600 border-green-200 hover:bg-green-50"
                              onClick={() => updateFeedbackMutation.mutate({ id: fb.id, status: "reviewed" })}
                              disabled={updateFeedbackMutation.isPending}
                            >
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Mark Reviewed
                            </Button>
                          )}
                          {fb.status !== "archived" && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-gray-600 border-gray-200 hover:bg-gray-50"
                              onClick={() => updateFeedbackMutation.mutate({ id: fb.id, status: "archived" })}
                              disabled={updateFeedbackMutation.isPending}
                            >
                              <Archive className="w-3 h-3 mr-1" />
                              Archive
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => {
                              if (confirm("Are you sure you want to delete this feedback?")) {
                                deleteFeedbackMutation.mutate(fb.id);
                              }
                            }}
                            disabled={deleteFeedbackMutation.isPending}
                          >
                            <Trash2 className="w-3 h-3 mr-1" />
                            Delete
                          </Button>
                        </div>
                      )}

                      {fb.reviewedAt && fb.reviewer && (
                        <p className="text-xs text-gray-400">
                          Reviewed by {fb.reviewer.firstName} {fb.reviewer.lastName} on {formatDate(fb.reviewedAt)}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
                {feedbackData?.data?.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    No feedback found
                  </div>
                )}
              </div>
            )}

            {/* Pagination Controls */}
            {!feedbackLoading && feedbackData?.pagination && (
              <div className="flex items-center justify-between mt-4 px-2 pt-4 border-t">
                <div className="text-sm text-gray-500">
                  Showing {feedbackPage * itemsPerPage + 1} to {Math.min((feedbackPage + 1) * itemsPerPage, feedbackData.pagination.total)} of {feedbackData.pagination.total}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setFeedbackPage(p => Math.max(0, p - 1))}
                    disabled={feedbackPage === 0}
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setFeedbackPage(p => p + 1)}
                    disabled={(feedbackPage + 1) * itemsPerPage >= (feedbackData.pagination.total || 0)}
                  >
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Unlock Requests Tab */}
          <TabsContent value="unlock-requests" className="space-y-4">
            <div className="flex items-center gap-2 text-sm text-gray-500 bg-yellow-50 p-3 rounded-lg">
              <Lock className="w-4 h-4 text-yellow-600" />
              <span>Manage unlock requests and view all locked user accounts</span>
            </div>

            {/* Card 1: Unlock Requests */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-yellow-600" />
                  Unlock Requests
                  {(unlockRequestsData?.data?.length ?? 0) > 0 && (
                    <Badge variant="secondary" className="ml-2 bg-yellow-100 text-yellow-800">
                      {unlockRequestsData?.data?.length}
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {unlockRequestsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
                  </div>
                ) : (
                  <div className="space-y-3">
                    {unlockRequestsData?.data?.map((request: UnlockRequest) => (
                  <Card key={request.id} className="border-yellow-200 bg-yellow-50/30">
                    <CardContent className="p-4">
                      <div className="space-y-3">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                              <Lock className="w-5 h-5 text-yellow-600" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-gray-900">
                                  {request.user?.firstName && request.user?.lastName
                                    ? `${request.user.firstName} ${request.user.lastName}`
                                    : request.userEmail || "Unknown User"}
                                </p>
                                <Badge variant="secondary" className="bg-yellow-100 text-yellow-800 text-xs">
                                  <Lock className="w-3 h-3 mr-1" />
                                  Locked
                                </Badge>
                              </div>
                              {request.userEmail && (
                                <p className="text-sm text-gray-500">{request.userEmail}</p>
                              )}
                              <p className="text-xs text-gray-400 mt-1">
                                <Calendar className="w-3 h-3 inline mr-1" />
                                Request submitted: {formatDate(request.createdAt)}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Lock reason (why they were locked) */}
                        {request.lockReason && (
                          <div className="text-sm bg-red-50 p-3 rounded-lg border border-red-100">
                            <p className="font-medium text-red-800 mb-1 flex items-center gap-1">
                              <AlertCircle className="w-4 h-4" />
                              Reason for Lock:
                            </p>
                            <p className="text-red-700">{request.lockReason}</p>
                          </div>
                        )}

                        {/* User's message (their unlock request) */}
                        {request.lockDetails && (
                          <div className="text-sm bg-blue-50 p-3 rounded-lg border border-blue-100">
                            <p className="font-medium text-blue-800 mb-1 flex items-center gap-1">
                              <MessageSquare className="w-4 h-4" />
                              User's Message:
                            </p>
                            <p className="text-blue-700">{request.lockDetails}</p>
                          </div>
                        )}

                        {/* Current lock info from user profile */}
                        {request.user && (
                          <div className="text-xs text-gray-500 border-t pt-2 mt-2">
                            <div className="flex items-center gap-4">
                              {request.user.lockedAt && (
                                <span>
                                  <Clock className="w-3 h-3 inline mr-1" />
                                  Locked: {formatDate(request.user.lockedAt)}
                                </span>
                              )}
                              {request.user.lockReason && (
                                <span className="text-red-600">
                                  Original Reason: {request.user.lockReason}
                                </span>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Actions */}
                        {canEdit && (
                          <div className="flex gap-2 pt-2 border-t">
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-green-600 border-green-200 hover:bg-green-50"
                              onClick={() => {
                                handleUnlockAccount(request.userId);
                                // Also dismiss the request after unlocking
                                dismissUnlockRequestMutation.mutate(request.id);
                              }}
                              disabled={unlockUserMutation.isPending || dismissUnlockRequestMutation.isPending}
                            >
                              <Unlock className="w-4 h-4 mr-1" />
                              Unlock Account
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-gray-600 border-gray-200 hover:bg-gray-50"
                              onClick={() => {
                                if (confirm("Dismiss this unlock request without unlocking the account?")) {
                                  dismissUnlockRequestMutation.mutate(request.id);
                                }
                              }}
                              disabled={dismissUnlockRequestMutation.isPending}
                            >
                              <Trash2 className="w-3 h-3 mr-1" />
                              Dismiss
                            </Button>
                            {request.userEmail && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-blue-600 border-blue-200 hover:bg-blue-50"
                                onClick={() => {
                                  const subject = encodeURIComponent("Regarding Your Account Unlock Request");
                                  const body = encodeURIComponent(
                                    `Hi ${request.user?.firstName || "there"},\n\nWe've reviewed your unlock request.\n\n`
                                  );
                                  window.open(`mailto:${request.userEmail}?subject=${subject}&body=${body}`, "_blank");
                                }}
                              >
                                <Mail className="w-3 h-3 mr-1" />
                                Email User
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                    ))}
                    {(unlockRequestsData?.data?.length ?? 0) === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        <Unlock className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                        No unlock requests pending
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Card 2: All Locked Users */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Lock className="w-5 h-5 text-red-600" />
                  All Locked Users
                  {usersData?.data?.filter((u: UserWithActivity) => u.isLocked).length > 0 && (
                    <Badge variant="secondary" className="ml-2 bg-red-100 text-red-800">
                      {usersData?.data?.filter((u: UserWithActivity) => u.isLocked).length}
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Search for locked users */}
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search locked users by name or email..."
                    value={lockedUsersSearch}
                    onChange={(e) => setLockedUsersSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>

                {usersLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
                  </div>
                ) : (
                  <div className="space-y-3">
                    {usersData?.data
                      ?.filter((userData: UserWithActivity) => {
                        // Only show locked users
                        if (!userData.isLocked) return false;

                        // Apply search filter
                        if (lockedUsersSearch) {
                          const searchLower = lockedUsersSearch.toLowerCase();
                          const fullName = `${userData.firstName || ''} ${userData.lastName || ''}`.toLowerCase();
                          const email = (userData.email || '').toLowerCase();
                          return fullName.includes(searchLower) || email.includes(searchLower);
                        }

                        return true;
                      })
                      ?.map((userData: UserWithActivity) => (
                        <Card key={userData.id} className="border-red-200 bg-red-50/30">
                          <CardContent className="p-4">
                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                              {/* Left side: User info */}
                              <div className="flex items-start gap-3 flex-1 min-w-0">
                                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                                  <Lock className="w-5 h-5 text-red-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <p className="font-medium text-gray-900 truncate">
                                      {userData.firstName && userData.lastName
                                        ? `${userData.firstName} ${userData.lastName}`
                                        : userData.email || "Unknown User"}
                                    </p>
                                    <Badge variant="secondary" className="bg-red-100 text-red-800 text-xs">
                                      <Lock className="w-3 h-3 mr-1" />
                                      Locked
                                    </Badge>
                                  </div>
                                  <p className="text-sm text-gray-500 truncate">{userData.email}</p>
                                  <div className="flex items-center gap-3 mt-1 text-xs text-gray-400 flex-wrap">
                                    <span className="flex items-center gap-1">
                                      <Calendar className="w-3 h-3" />
                                      {formatDate(userData.createdAt)}
                                    </span>
                                    {userData.roles && (
                                      <Badge variant="outline" className="text-xs">
                                        {userData.roles.name}
                                      </Badge>
                                    )}
                                  </div>
                                  {/* Show lock details */}
                                  <div className="mt-2 p-2 bg-red-100 rounded text-xs space-y-1">
                                    {userData.lockReason && (
                                      <p className="text-red-800">
                                        <span className="font-medium">Reason:</span> {userData.lockReason}
                                      </p>
                                    )}
                                    {userData.lockExpiresAt && (
                                      <p className="text-red-700">
                                        <span className="font-medium">Expires:</span> {formatDate(userData.lockExpiresAt)}
                                      </p>
                                    )}
                                    {userData.requiresAdminApproval && (
                                      <p className="text-red-800 font-medium flex items-center gap-1">
                                        <AlertCircle className="w-3 h-3" />
                                        Requires admin approval to unlock
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Right side: Actions */}
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-green-600 border-green-200 hover:bg-green-50"
                                  onClick={() => handleUnlockAccount(userData.id)}
                                  disabled={unlockUserMutation.isPending}
                                >
                                  <Unlock className="w-4 h-4 mr-1" />
                                  Unlock
                                </Button>
                                {userData.email && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-blue-600 border-blue-200 hover:bg-blue-50"
                                    onClick={() => {
                                      const subject = encodeURIComponent("Regarding Your Locked Account");
                                      const body = encodeURIComponent(
                                        `Hi ${userData.firstName || "there"},\n\nWe're reaching out about your locked account.\n\n`
                                      );
                                      window.open(`mailto:${userData.email}?subject=${subject}&body=${body}`, "_blank");
                                    }}
                                  >
                                    <Mail className="w-3 h-3 mr-1" />
                                    Email
                                  </Button>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    {usersData?.data?.filter((u: UserWithActivity) => u.isLocked).length === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        <Lock className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                        No locked users found
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Lookups Tab */}
          {/* Card 19 task B1 — comped plan grants. Self-contained: it owns its own queries so
              it can be lifted out of this 3,000-line page later without untangling anything. */}
          <TabsContent value="comps" className="space-y-4">
            <PlanGrantsPanel />
          </TabsContent>

          <TabsContent value="lookups" className="space-y-6">
            <div className="flex items-center gap-2 text-sm text-gray-500 bg-blue-50 p-3 rounded-lg">
              <List className="w-4 h-4 text-blue-600" />
              <span>Manage lookup tables used across the platform (primary trades, market types, industries)</span>
            </div>

            {/* Primary Trades Section */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                    <List className="w-4 h-4 text-orange-600" />
                  </div>
                  Primary Trades
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Add new primary trade form */}
                {canEdit && (
                  <div className="flex gap-2">
                    <Input
                      placeholder="Enter new primary trade name..."
                      value={newPrimaryTradeName}
                      onChange={(e) => setNewPrimaryTradeName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && newPrimaryTradeName.trim()) {
                          createPrimaryTradeMutation.mutate(newPrimaryTradeName.trim());
                        }
                      }}
                      className="flex-1"
                    />
                    <Select value={newPrimaryTradeSection} onValueChange={setNewPrimaryTradeSection}>
                      <SelectTrigger className="w-56" aria-label="Section">
                        <SelectValue placeholder="Section" />
                      </SelectTrigger>
                      <SelectContent>
                        {primaryTradeSections.map((section) => (
                          <SelectItem key={section} value={section}>
                            {section}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      onClick={() => {
                        if (newPrimaryTradeName.trim()) {
                          createPrimaryTradeMutation.mutate(newPrimaryTradeName.trim());
                        }
                      }}
                      disabled={!newPrimaryTradeName.trim() || createPrimaryTradeMutation.isPending}
                      className="bg-orange-600 hover:bg-orange-700"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add
                    </Button>
                  </div>
                )}

                {/* Primary trades list */}
                {primaryTradesLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="w-5 h-5 animate-spin text-gray-400" />
                  </div>
                ) : (
                  <div className="space-y-2">
                    {primaryTradesData?.data
                      ?.slice()
                      .sort((a: PrimaryTrade, b: PrimaryTrade) => a.name.localeCompare(b.name))
                      .map((trade: PrimaryTrade) => (
                      <div
                        key={trade.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3 flex-1">
                          <span className="text-sm font-medium text-gray-900">{trade.name}</span>
                          <span className="text-xs text-gray-500">{trade.section || "No section"}</span>
                          <span className="text-xs text-gray-500">
                            ({trade.totalUsage || 0} user{(trade.totalUsage || 0) !== 1 ? 's' : ''})
                          </span>
                        </div>
                        {canEdit && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => setDeletePrimaryTradeModal({ isOpen: true, item: trade })}
                            disabled={deletePrimaryTradeMutation.isPending}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                    {(primaryTradesData?.data?.length ?? 0) === 0 && (
                      <div className="text-center py-4 text-gray-500 text-sm">
                        No primary trades found
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Market Types Section */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                    <List className="w-4 h-4 text-green-600" />
                  </div>
                  Market Types
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Add new market type form */}
                {canEdit && (
                  <div className="flex gap-2">
                    <Input
                      placeholder="Enter new market type name..."
                      value={newMarketTypeName}
                      onChange={(e) => setNewMarketTypeName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && newMarketTypeName.trim()) {
                          createMarketTypeMutation.mutate(newMarketTypeName.trim());
                        }
                      }}
                      className="flex-1"
                    />
                    <Button
                      onClick={() => {
                        if (newMarketTypeName.trim()) {
                          createMarketTypeMutation.mutate(newMarketTypeName.trim());
                        }
                      }}
                      disabled={!newMarketTypeName.trim() || createMarketTypeMutation.isPending}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add
                    </Button>
                  </div>
                )}

                {/* Market types list */}
                {marketTypesLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="w-5 h-5 animate-spin text-gray-400" />
                  </div>
                ) : (
                  <div className="space-y-2">
                    {marketTypesData?.data
                      ?.slice()
                      .sort((a: MarketType, b: MarketType) => a.name.localeCompare(b.name))
                      .map((marketType: MarketType) => (
                      <div
                        key={marketType.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-medium text-gray-900">{marketType.name}</span>
                        </div>
                        {canEdit && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => setDeleteMarketTypeModal({ isOpen: true, item: marketType })}
                            disabled={deleteMarketTypeMutation.isPending}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                    {(marketTypesData?.data?.length ?? 0) === 0 && (
                      <div className="text-center py-4 text-gray-500 text-sm">
                        No market types found
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Industries Section */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                    <List className="w-4 h-4 text-purple-600" />
                  </div>
                  Industries
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Add new industry form */}
                {canEdit && (
                  <div className="flex gap-2">
                    <Input
                      placeholder="Enter new industry name..."
                      value={newIndustryName}
                      onChange={(e) => setNewIndustryName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && newIndustryName.trim()) {
                          createIndustryMutation.mutate(newIndustryName.trim());
                        }
                      }}
                      className="flex-1"
                    />
                    <Button
                      onClick={() => {
                        if (newIndustryName.trim()) {
                          createIndustryMutation.mutate(newIndustryName.trim());
                        }
                      }}
                      disabled={!newIndustryName.trim() || createIndustryMutation.isPending}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add
                    </Button>
                  </div>
                )}

                {/* Industries list */}
                {industriesLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="w-5 h-5 animate-spin text-gray-400" />
                  </div>
                ) : (
                  <div className="space-y-2">
                    {industriesData?.data
                      ?.slice()
                      .sort((a: Industry, b: Industry) => a.name.localeCompare(b.name))
                      .map((industry: Industry) => (
                      <div
                        key={industry.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-medium text-gray-900">{industry.name}</span>
                        </div>
                        {canEdit && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => setDeleteIndustryModal({ isOpen: true, item: industry })}
                            disabled={deleteIndustryMutation.isPending}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                    {(industriesData?.data?.length ?? 0) === 0 && (
                      <div className="text-center py-4 text-gray-500 text-sm">
                        No industries found
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Account Action Wizard */}
      <AccountActionWizard
        isOpen={wizardOpen}
        onClose={() => {
          setWizardOpen(false);
          setSelectedUser(null);
        }}
        user={selectedUser ? {
          id: selectedUser.id,
          fullName: selectedUser.firstName && selectedUser.lastName
            ? `${selectedUser.firstName} ${selectedUser.lastName}`
            : selectedUser.email || "User",
          firstName: selectedUser.firstName,
          lastName: selectedUser.lastName,
          email: selectedUser.email ?? undefined,  // Use nullish coalescing to preserve empty string but convert null
        } : null}
        onLockAccount={handleLockAccount}
        onDeleteAccount={handleDeleteAccount}
        isLoading={lockUserMutation.isPending || permanentDeleteMutation.isPending}
      />

      {/* Delete Market Type Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={deleteMarketTypeModal.isOpen}
        onClose={() => setDeleteMarketTypeModal({ isOpen: false, item: null })}
        onConfirm={() => {
          if (deleteMarketTypeModal.item) {
            deleteMarketTypeMutation.mutate(deleteMarketTypeModal.item.id);
            setDeleteMarketTypeModal({ isOpen: false, item: null });
          }
        }}
        itemName="Market Type"
        message={
          <>
            Are you sure you want to delete <strong>{deleteMarketTypeModal.item?.name}</strong>?
            <br />
            <br />
            This may affect user profiles that reference this market type.
          </>
        }
        confirmLoading={deleteMarketTypeMutation.isPending}
      />

      {/* Delete Industry Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={deleteIndustryModal.isOpen}
        onClose={() => setDeleteIndustryModal({ isOpen: false, item: null })}
        onConfirm={() => {
          if (deleteIndustryModal.item) {
            deleteIndustryMutation.mutate(deleteIndustryModal.item.id);
            setDeleteIndustryModal({ isOpen: false, item: null });
          }
        }}
        itemName="Industry"
        message={
          <>
            Are you sure you want to delete <strong>{deleteIndustryModal.item?.name}</strong>?
            <br />
            <br />
            This may affect user profiles that reference this industry.
          </>
        }
        confirmLoading={deleteIndustryMutation.isPending}
      />

      {/* Delete Primary Trade Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={deletePrimaryTradeModal.isOpen}
        onClose={() => setDeletePrimaryTradeModal({ isOpen: false, item: null })}
        onConfirm={() => {
          if (deletePrimaryTradeModal.item) {
            deletePrimaryTradeMutation.mutate(deletePrimaryTradeModal.item.id);
            setDeletePrimaryTradeModal({ isOpen: false, item: null });
          }
        }}
        itemName="Primary Trade"
        message={
          <>
            Are you sure you want to delete <strong>{deletePrimaryTradeModal.item?.name}</strong>?
            <br />
            <br />
            {deletePrimaryTradeModal.item?.totalUsage ? (
              <span className="text-red-600 font-medium">
                This trade is currently in use by {deletePrimaryTradeModal.item.userCount || 0} user(s).
                Deletion will be blocked.
              </span>
            ) : (
              <span>This trade is not currently in use and can be safely deleted.</span>
            )}
          </>
        }
        confirmLoading={deletePrimaryTradeMutation.isPending}
      />

      {/* Referral Detail Modal */}
      {selectedReferral && (
        <ReferralDetailModal
          referral={{
            // Spread all fields from selectedReferral to include all data
            ...(selectedReferral as any),
            // Override/format specific fields
            id: parseInt(selectedReferral.id),
            senderName: selectedReferral.sender
              ? `${selectedReferral.sender.firstName || ''} ${selectedReferral.sender.lastName || ''}`.trim()
              : undefined,
            receiverName: selectedReferral.receiver
              ? `${selectedReferral.receiver.firstName || ''} ${selectedReferral.receiver.lastName || ''}`.trim()
              : undefined,
            // Map API field names to modal expected field names
            status: (selectedReferral as any).status || getStatusFromId((selectedReferral as any).statusId),
            referralAttachments: (selectedReferral as any).referralAttachments,
            // Ensure optional fields are undefined instead of null for cleaner rendering
            clientName: selectedReferral.clientName || undefined,
            clientEmail: selectedReferral.clientEmail || undefined,
            clientPhone: selectedReferral.clientPhone || undefined,
            completedAt: selectedReferral.completedAt || undefined,
            jobDescription: selectedReferral.jobDescription || undefined,
          }}
          isOpen={referralModalOpen}
          onClose={() => {
            setReferralModalOpen(false);
            setSelectedReferral(null);
          }}
        />
      )}

      {/* User Permissions Modal */}
      {selectedUserForPermissions && (
        <UserPermissionsModal
          isOpen={permissionsModalOpen}
          onClose={() => {
            setPermissionsModalOpen(false);
            setSelectedUserForPermissions(null);
          }}
          user={{
            id: selectedUserForPermissions.id,
            firstName: selectedUserForPermissions.firstName,
            lastName: selectedUserForPermissions.lastName,
            email: selectedUserForPermissions.email,
            roleId: selectedUserForPermissions.roles?.id || null,
          }}
        />
      )}

      {/* Confirm-email confirmation. Bypassing email verification is an assertion about a real
          person, so it does not fire straight off a menu click. */}
      <AlertDialog
        open={userPendingEmailConfirm !== null}
        onOpenChange={(open) => !open && setUserPendingEmailConfirm(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm this email address?</AlertDialogTitle>
            <AlertDialogDescription>
              This marks{" "}
              <span className="font-medium">
                {userPendingEmailConfirm?.email || "this user"}
              </span>{" "}
              as verified without them clicking anything, and lets them sign in immediately.
              Only do this once you have confirmed by other means — a call, a text, a reply from
              a known address — that this person owns the mailbox. The action is recorded
              against your account.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-green-600 hover:bg-green-700"
              onClick={confirmEmailConfirmed}
              disabled={confirmEmailMutation.isPending}
            >
              Confirm Email
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
